import React, { useState, useEffect } from 'react';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { PAST_PRESIDENTS } from '../data/mock';
import { Check, Quote, ArrowUpRight, Star } from 'lucide-react';
import { api } from '../lib/api';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';

// Adapt DB row {data, sort_order, ...} to public-shape used by this page.
// Missing fields fall back to safe defaults so the render never crashes.
function adaptPresident(row) {
  const d = row.data || {};
  return {
    id: row.id,
    term: d.period || d.term || '',
    name: d.name || '',
    image: d.image || '',
    title: d.title || d.role || '',
    bio: d.bio || '',
    highlights: Array.isArray(d.highlights) ? d.highlights : [],
    quote: d.quote || '',
    years: typeof d.years === 'number' ? d.years : (d.years ? Number(d.years) || 0 : 0),
  };
}

export default function Presidents() {
  useSEO('presidents');
  const [active, setActive] = useState(0);
  const [list, setList] = useState(PAST_PRESIDENTS);

  useEffect(() => {
    api.publicContent('presidents').then(rows => {
      if (Array.isArray(rows) && rows.length > 0) setList(rows.map(adaptPresident));
    }).catch(() => {});
  }, []);

  // Guard against an empty list — fall back to the built-in mock so the page never crashes.
  const safeList = list && list.length > 0 ? list : PAST_PRESIDENTS;
  const p = safeList[active] || safeList[0];

  return (
    <EditRegion label="Başkanlar" to="/admin/presidents">
      <>
      <PageHero slug="presidents"
        kicker="Başkanlarımız"
        title={<>Bugünü şekillendiren <span className="italic text-coral-200">liderler</span>.</>}
        text="1992'den günümüze vakfımızı yöneten başkanlarımız; her biri YEKÜV'ün hikâyesine farklı bir renk katmış değerli liderlerimizdir."
      />

      {/* Featured president */}
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Photo */}
            <div className="lg:col-span-6 relative">
              <div className="relative rounded-[32px] overflow-hidden shadow-2xl shadow-ink-900/25 aspect-[4/5] bg-ink-700">
                <img
                  key={p.id}
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover fade-up"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-transparent to-transparent" />
                {active === 0 && (
                  <div className="absolute top-6 right-6">
                    <span className="inline-flex items-center gap-1.5 bg-gold text-ink-700 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">
                      <Star className="w-3.5 h-3.5" /> Kurucu Başkan
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-8 text-cream">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-2">{p.title}</div>
                  <div className="font-display text-3xl md:text-4xl leading-tight">{p.name}</div>
                  <div className="mt-2 text-cream/75 text-sm tabular">{p.term}</div>
                </div>
              </div>

            </div>

            {/* Detail */}
            <div className="lg:col-span-6">
              <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] leading-[1.02] text-ink-600">{p.name}</h2>
              <div className="mt-2 text-lg text-coral-300 font-medium">{p.title}</div>

              <Quote className="w-10 h-10 text-gold mt-8" />
              <p className="mt-4 text-ink-500 text-lg leading-relaxed">{p.bio}</p>

              {/* Highlights */}
              <div className="mt-8">
                <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold mb-4">Öne Çıkanlar</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {p.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-cream-200 text-sm text-ink-600">
                      <div className="w-6 h-6 rounded-full bg-gold/20 text-gold-500 grid place-items-center shrink-0"><Check className="w-3.5 h-3.5" /></div>
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex gap-2">
                <button
                  onClick={() => setActive((active - 1 + safeList.length) % safeList.length)}
                  className="w-11 h-11 rounded-full border border-ink-200 grid place-items-center text-ink-600 hover:bg-ink-600 hover:text-cream hover:border-ink-600 transition-colors"
                  aria-label="Önceki"
                >←</button>
                <button
                  onClick={() => setActive((active + 1) % safeList.length)}
                  className="w-11 h-11 rounded-full border border-ink-200 grid place-items-center text-ink-600 hover:bg-ink-600 hover:text-cream hover:border-ink-600 transition-colors"
                  aria-label="Sonraki"
                >→</button>
                <div className="ml-4 text-sm text-ink-400 tabular flex items-center">0{active + 1} / 0{safeList.length}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial tribute section */}
      <section className="bg-cream-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <InlineText settingKey="presidents_miras_kicker" fallback="Miras" as="div"
              className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
            <InlineText settingKey="presidents_miras_title" fallback="Üç başkan, bir aile." as="h2"
              multiline className="font-display text-4xl md:text-5xl mt-3 text-ink-600 leading-[1.05]" />
            <InlineText settingKey="presidents_miras_text" fallback={"Vakfımız 33 yıllık yolculuğunda üç değerli liderimizle büyüdü. Her biri YEKÜV'ün hikâyesine kendi rengini kattı; bıraktıkları miras bugünkü ailemizin temelidir."} as="p"
              multiline className="mt-5 text-ink-500 leading-relaxed" />
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {safeList.map((pp, i) => (
              <button
                key={pp.id}
                onClick={() => { setActive(i); window.scrollTo({ top: 420, behavior: 'smooth' }); }}
                className={`group relative text-left bg-white rounded-3xl overflow-hidden border transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-ink-900/10 ${active === i ? 'border-gold ring-2 ring-gold/20' : 'border-ink-100'}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-ink-50">
                  <img src={pp.image} alt={pp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-ink-900 via-ink-900/70 to-transparent text-cream">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">{pp.term}</div>
                  <div className="font-display text-2xl md:text-[26px] mt-1 leading-tight">{pp.name}</div>
                  <div className="text-cream/70 text-xs mt-1">{pp.title}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="relative overflow-hidden rounded-[32px] bg-ink-600 text-cream p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <InlineText settingKey="presidents_cta_kicker" fallback="Hikâyemiz" as="div"
                className="text-[11px] tracking-[0.3em] uppercase text-gold font-semibold" />
              <InlineText settingKey="presidents_cta_title" fallback="33 yıllık yolculuğumuzun tamamını keşfedin." as="h3"
                multiline className="font-display text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight" />
              <InlineText settingKey="presidents_cta_text" fallback="Kurumsal tarihimiz, kilometre taşlarımız ve yönetim yapımız hakkında daha fazla bilgi için tarihçe sayfamızı ziyaret edin." as="p"
                multiline className="mt-4 text-cream/75" />
            </div>
            <div className="relative flex md:justify-end">
              <Link to="/kurumsal" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-14 font-semibold hover:bg-gold-400 transition-colors">
                Tarihçeyi İncele <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}
